'use client';

import { useEffect, useState } from 'react';
import DotMatrixDisplay from './DotMatrixDisplay';
import { makeSessionId } from './sessionId';
import styles from './smart-link.module.css';
import { formatClickCount, recordSmartLinkClick } from '@/lib/services/smartLinkStats';
import { detectPlatform, type Platform } from '@/lib/platform';
import { trackEvent } from '@/lib/analytics';

interface Props {
  slug: string;
  name: string;
  websiteUrl: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}

const PLATFORM_LABEL: Record<Platform, string> = {
  ios: 'IOS',
  android: 'ANDROID',
  desktop: 'WEB',
};

const STATUS_FLOW: Record<Platform, string[]> = {
  ios: ['DETECTING PLATFORM', 'IOS · ROUTING TO APP STORE'],
  android: ['DETECTING PLATFORM', 'ANDROID · ROUTING TO PLAY STORE'],
  desktop: ['DETECTING PLATFORM', 'WEB · ROUTING TO SITE'],
};

export default function SmartRedirect({
  slug,
  name,
  websiteUrl,
  appStoreUrl,
  playStoreUrl,
}: Props) {
  const [platform, setPlatform] = useState<Platform>('desktop');
  const [target, setTarget] = useState<string | null>(null);
  const [statusIdx, setStatusIdx] = useState(0);
  const [clicks, setClicks] = useState<number | null>(null);

  // A short, fixed session ID just for the terminal flavor in the corner.
  // Starts as a placeholder so prerendered HTML matches the first client
  // render; the real ID is generated post-mount (hydration-safe).
  const [sessionId, setSessionId] = useState('······');

  useEffect(() => {
    let dest = websiteUrl;

    const init = () => {
      setSessionId(makeSessionId());

      const p = detectPlatform();
      setPlatform(p);

      if (p === 'ios' && appStoreUrl) dest = appStoreUrl;
      else if (p === 'android' && playStoreUrl) dest = playStoreUrl;
      setTarget(dest);

      // One analytics event per visit identifying the platform branch taken.
      const eventName =
        p === 'ios'
          ? 'smartlink_click_ios'
          : p === 'android'
            ? 'smartlink_click_android'
            : 'smartlink_click_desktop';
      trackEvent(eventName, { slug });
    };
    init();

    // Debug bypass: ?stay=1 disables the redirect for local/preview QA.
    const stay =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('stay') === '1';

    // Convergence completes at ~700ms; status flips at 720ms.
    const t1 = setTimeout(() => setStatusIdx(1), 720);

    // Hard fallback redirect at 1.8s. The counter usually resolves within
    // 600-800ms and triggers an earlier "soft redirect" once it's visible.
    let didRedirect = false;
    const goNow = () => {
      if (didRedirect || stay) return;
      didRedirect = true;
      window.location.replace(dest);
    };

    const fallback = stay ? null : setTimeout(goNow, 1800);

    // Counter: fire-and-forget. When it resolves, render it and then schedule
    // a soft redirect 500ms later so the user actually sees the number.
    let softTimer: ReturnType<typeof setTimeout> | null = null;
    recordSmartLinkClick(slug).then((n) => {
      if (n != null) {
        setClicks(n);
        if (!stay) softTimer = setTimeout(goNow, 500);
      }
    });

    return () => {
      clearTimeout(t1);
      if (fallback != null) clearTimeout(fallback);
      if (softTimer != null) clearTimeout(softTimer);
    };
  }, [slug, websiteUrl, appStoreUrl, playStoreUrl]);

  const statusLines = STATUS_FLOW[platform];

  return (
    <main className={styles.shell}>
      <header className={styles.header}>
        <span className={styles.badge}>SIGNAL ACQUIRED</span>
        <span className={styles.meta}>
          DATACODE / {slug.toUpperCase()} · {PLATFORM_LABEL[platform]} · {sessionId}
        </span>
      </header>

      <section className={styles.stage}>
        <div className={styles.matrixFrame}>
          <DotMatrixDisplay text={name} width={360} duration={700} />
        </div>

        <p className={styles.caption} aria-live="polite">
          — <b>opening {name.toLowerCase()}</b>. agents routing.
        </p>

        <p className={styles.status}>{statusLines[statusIdx]}</p>

        {clicks != null && (
          <div className={styles.counter}>
            <div className={styles.counterMatrix}>
              <DotMatrixDisplay text={formatClickCount(clicks)} width={160} duration={500} />
            </div>
            <p className={styles.counterLabel}>
              VISITOR <b>#{formatClickCount(clicks)}</b> · ALL TIME
            </p>
          </div>
        )}
      </section>

      <footer className={styles.rail}>
        <span className={styles.railHint}>If nothing happens, continue manually</span>
        <nav className={styles.links} aria-label="Manual destinations">
          {appStoreUrl && (
            <>
              <a className={styles.link} href={appStoreUrl}>App Store</a>
              {(playStoreUrl || websiteUrl) && <span className={styles.linkSep}>◆</span>}
            </>
          )}
          {playStoreUrl && (
            <>
              <a className={styles.link} href={playStoreUrl}>Google Play</a>
              {websiteUrl && <span className={styles.linkSep}>◆</span>}
            </>
          )}
          <a className={styles.link} href={target ?? websiteUrl}>
            {target === websiteUrl ? 'Continue' : 'Website'}
          </a>
        </nav>
      </footer>
    </main>
  );
}
